import { useEffect, useState, useCallback } from 'react';
import { IPurchaseOffering, IPurchaseResult, IPurchaseStatus } from '@datatype/type';
import { WEB_TO_APP, APP_TO_WEB } from '@constant/common/bridge-event';
import { LOCAL_STORAGE_USER_KEY } from '@config/auth';
import { sendApplicationEventByBridge, receiveApplicationEventByBridge } from '@constant/common/bridge-event';
import { useTypedSelector } from '@hook/common/useStore';
import { ISubscriptionData, EApiSubscriptionStatus } from '@datatype/billing/api-type';
import { useMySubscriptionQuery, useSyncMySubscriptionMutation } from '@hook/billing/useApi';
import { EBILLING_QUERY_KEY } from '@constant/billing/query-key';
import { queryClient } from '@config/query-client';
import { IApiResponse } from '@datatype/common/api-type';
import { IS_MOBILE_MOCKING } from '@constant/iyagigo/variable';

// DEV 환경 브라우저용 목 데이터
const MOCK_PURCHASE_OFFERING: IPurchaseOffering[] = [
  {
    identifier: '$rc_monthly',
    productId: 'monthly_plan',
    priceString: '₩5,900',
    price: 5900,
    currencyCode: 'KRW',
    title: '월간 구독',
    description: '모든 프리미엄 기능을 이용할 수 있습니다',
    period: 'monthly',
  },
  {
    identifier: '$rc_annual',
    productId: 'yearly_plan',
    priceString: '₩39,000',
    price: 39000,
    currencyCode: 'KRW',
    title: '연간 구독',
    description: '모든 프리미엄 기능을 이용할 수 있습니다',
    period: 'annual',
  },
];

// const isMock = process.env.NODE_ENV === 'development';
// IS_TEST=true이면 mock 데이터 사용 (브라우저에서 백엔드 없이 구독 흐름 검증)
const isMock = IS_MOBILE_MOCKING;

// 백엔드 ISubscriptionData → UI용 IPurchaseStatus
const toStatus = (data: ISubscriptionData | null | undefined): IPurchaseStatus => {
  if (!data) {
    return { isActive: false, plan: null, expirationDate: null, willRenew: false };
  }
  return {
    isActive: data.status === EApiSubscriptionStatus.ACTIVE,
    plan: data.plan ?? null,
    expirationDate: data.expiresAt,
    willRenew: data.renewStatus === EApiSubscriptionStatus.ACTIVE,
  };
};

/**
 * 인앱 구독 관리 훅
 * - 앱(React Native) 환경: postMessage로 RN에 요청 → CustomEvent로 결과 수신
 * - 브라우저 DEV 환경: 목 데이터로 UI 개발 가능
 */
export const useApplicationPurchase = () => {
  const isAuthenticated = useTypedSelector((state) => state.rootReducer.userReducer.isAuthenticated);
  const subscription = useMySubscriptionQuery();
  const status = toStatus(subscription.data?.data);
  const syncMutation = useSyncMySubscriptionMutation();
  const [offerings, setPurchaseOffering] = useState<IPurchaseOffering[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // IS_TEST=true이면 앱 환경으로 시뮬레이션 (브라우저에서 앱 전용 흐름 검증)
  const isApp = !!window.ReactNativeWebView || IS_MOBILE_MOCKING;

  // 상품 목록 조회
  const fetchPurchaseOffering = useCallback(async () => {
    try {
      if (isMock) return setPurchaseOffering(MOCK_PURCHASE_OFFERING);
      if (!isApp) return;
      setIsLoading(true);
      sendApplicationEventByBridge(WEB_TO_APP.FETCH_PURCHASE_OFFERING);
      const result = await receiveApplicationEventByBridge<IPurchaseOffering[]>(APP_TO_WEB.PURCHASE_OFFERING);
      setPurchaseOffering(result);
    } catch (error) {
      console.error('=== fetchPurchaseOffering failed ===', error);
    } finally {
      setIsLoading(false);
    }
  }, [isApp]);

  // 구독 상태 조회 + 백엔드 동기화
  // - RN에서 받은 isActive는 백엔드 sync 트리거 용도
  // - 화면 status는 useMySubscriptionQuery 캐시(백엔드 DB값) source of truth
  // - mutation onSuccess가 캐시를 자동 갱신
  const fetchPurchaseStatus = useCallback(async () => {
    try {
      if (isMock) return;
      if (!isApp) return;
      const userKey = localStorage.getItem(LOCAL_STORAGE_USER_KEY);
      if (!isAuthenticated || !userKey) return;
      sendApplicationEventByBridge(WEB_TO_APP.FETCH_PURCHASE_STATUS, { userKey });
      const result = await receiveApplicationEventByBridge<IPurchaseStatus>(APP_TO_WEB.PURCHASE_STATUS);
      await syncMutation.mutateAsync(result.isActive ? EApiSubscriptionStatus.ACTIVE : EApiSubscriptionStatus.INACTIVE);
    } catch (error) {
      console.error('=== fetchPurchaseStatus failed ===', error);
    }
  }, [isApp, isAuthenticated]);

  // 구매 요청
  const executePurchase = useCallback(
    async (productId: string): Promise<IPurchaseResult | null> => {
      try {
        setIsLoading(true);
        if (isMock) {
          // 실제 결제 흐름과 유사한 체감을 위해 지연 시뮬레이션
          await new Promise((resolve) => setTimeout(resolve, 3000));
          const now = new Date();
          const expirationDate = productId.includes('yearly')
            ? new Date(now.setFullYear(now.getFullYear() + 1))
            : new Date(now.setMonth(now.getMonth() + 1));
          const userKey = localStorage.getItem(LOCAL_STORAGE_USER_KEY) ?? '';
          queryClient.setQueryData<IApiResponse<ISubscriptionData | null>>(
            [EBILLING_QUERY_KEY.MY_SUBSCRIPTION, userKey],
            {
              status: 'success',
              data: {
                key: '',
                service: 'IYAGIGO',
                status: EApiSubscriptionStatus.ACTIVE,
                renewStatus: EApiSubscriptionStatus.ACTIVE,
                plan: productId,
                provider: '',
                startedAt: new Date().toISOString(),
                expiresAt: expirationDate.toISOString(),
              },
            },
          );
          return { status: 'success', productId };
        }

        if (!isApp) return null;
        const userKey = localStorage.getItem(LOCAL_STORAGE_USER_KEY);
        if (!isAuthenticated || !userKey) return null;

        sendApplicationEventByBridge(WEB_TO_APP.EXECUTE_PURCHASE, { productId, userKey });
        const result = await receiveApplicationEventByBridge<IPurchaseResult>(
          APP_TO_WEB.EXECUTE_PURCHASE_RESULT,
          120000,
        );
        // Purchase Success -> 백엔드 sync 트리거
        if (result.status === 'success') await fetchPurchaseStatus();
        return result;
      } catch (error) {
        console.error('=== executePurchase failed ===', error);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [isApp, fetchPurchaseStatus],
  );

  // 구매 복원
  const restorePurchase = useCallback(async (): Promise<IPurchaseResult | null> => {
    try {
      setIsLoading(true);
      if (isMock) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        return { status: 'error' };
      }
      if (!isApp) return null;
      const userKey = localStorage.getItem(LOCAL_STORAGE_USER_KEY);
      if (!isAuthenticated || !userKey) return null;
      sendApplicationEventByBridge(WEB_TO_APP.RESTORE_PURCHASE, { userKey });
      const result = await receiveApplicationEventByBridge<IPurchaseResult>(APP_TO_WEB.RESTORE_PURCHASE_RESULT);
      // 복원 요청이 정상 완료됐다면 활성 구독 유무와 관계없이 백엔드 상태 동기화
      // SDK/네트워크 오류는 error 정보가 포함되므로 동기화하지 않음
      const isRestored = result.status === 'success' || !result.error;
      if (isRestored) await fetchPurchaseStatus();
      return result;
    } catch (error) {
      console.error('=== restore failed ===', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [isApp, fetchPurchaseStatus]);

  // ###################################
  // 구독 상태 변경 리스너 (RevenueCat 자동 발화 이벤트 수신)
  //
  // executePurchase / restorePurchase / fetchPurchaseStatus는 "우리가 트리거하는" 케이스를 커버.
  // 본 리스너는 "우리가 트리거하지 않은" 상태 변경을 잡는 보험 역할:
  //   - App Store/Play Store에서 사용자가 직접 구독 취소
  //   - 자동 갱신 결제 성공/실패 (매달 자동 결제)
  //   - 결제 실패 → grace period 진입
  //   - 만료 도달
  //   - 앱 백그라운드 복귀 시 RevenueCat이 자체 polling으로 변경 감지
  //
  // RN(F35) usePurchaseListener의 Purchases.addCustomerInfoUpdateListener가
  // 위 변경들을 감지해 PURCHASE_STATUS 이벤트로 WebView에 전달 → 본 핸들러가 백엔드 sync 트리거.
  // ###################################
  useEffect(() => {
    if (!isApp) return;

    const handleStatusChange = (e: CustomEvent<IPurchaseStatus>) => {
      console.log('=== Subscription status changed ===', e.detail);
      // event listener는 비활성화 (너무 많은 변경이 있으므로)
      // syncMutation.mutate(e.detail.isActive ? EApiSubscriptionStatus.ACTIVE : EApiSubscriptionStatus.INACTIVE);
    };

    window.addEventListener(APP_TO_WEB.PURCHASE_STATUS, handleStatusChange as unknown as EventListener);
    return () => {
      window.removeEventListener(APP_TO_WEB.PURCHASE_STATUS, handleStatusChange as unknown as EventListener);
    };
  }, [isApp]);

  return {
    offerings,
    status,
    isLoading,
    isApp,
    executePurchase,
    restorePurchase,
    fetchPurchaseOffering,
    fetchPurchaseStatus,
  };
};

/**
 * 앱 진입 시 구독 상태 동기화 (IyagigoApp 루트 한 곳에서 사용)
 * - 결제 페이지 진입 안 한 사용자도 RN ↔ 백엔드 DB 부정합을 보정하기 위함
 * - 마운트 및 인증 상태 변경으로 fetchPurchaseStatus를 호출
 */
export const useSyncSubscription = () => {
  const { fetchPurchaseStatus } = useApplicationPurchase();

  useEffect(() => {
    void fetchPurchaseStatus();
  }, [fetchPurchaseStatus]);
};
